import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { CurrentUser, DailyExpense } from '../types';
import { dailyExpensesService } from '../lib/database';
import { uploadExpenseReceipt } from '../lib/expenseReceiptUpload';
import { getAI, getGenerativeModel, VertexAIBackend } from 'firebase/ai';
import { initializeFirebase } from '../lib/firebase';
import { GRANULAR_EXPENSE_CATEGORIES, migrateLegacyExpenseCategoryDisplay } from '../lib/granularExpenseCategories';

/** Parse OCR text from a receipt to extract amount, date, vendor/description, and line items note */
function parseReceiptText(text: string): {
  amount: string;
  date: string;
  description: string;
  vendor: string;
  itemsNote: string;
} {
  const result = { amount: '', date: '', description: '', vendor: '', itemsNote: '' };
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  // --- Amount: try to find the TOTAL line first, otherwise pick the largest amount ---
  type AmountCandidate = { value: number; index: number; isTotalLike: boolean };
  const amountCandidates: AmountCandidate[] = [];
  const totalRegex =
    /(total\b|amount due\b|grand total\b|balance(?:\s+(?:due|to pay))?|to pay\b|amt due\b)/i;
  const moneyRegex = /\$?\s*([0-9][0-9,]*\.\d{2})/g;

  lines.forEach((line, idx) => {
    const lower = line.toLowerCase();
    const isTotalLike = totalRegex.test(lower);
    const matches = [...line.matchAll(moneyRegex)];
    for (const m of matches) {
      const value = parseFloat(m[1].replace(/,/g, ''));
      if (!Number.isNaN(value)) {
        amountCandidates.push({ value, index: idx, isTotalLike });
      }
    }
  });

  let chosenAmount: AmountCandidate | undefined;
  const totalCandidates = amountCandidates.filter((c) => c.isTotalLike);
  if (totalCandidates.length) {
    // Prefer total-like amounts nearest the bottom; if tie, take the larger value
    chosenAmount = totalCandidates.reduce((best, curr) => {
      if (!best) return curr;
      if (curr.index > best.index) return curr;
      if (curr.index === best.index && curr.value > best.value) return curr;
      return best;
    });
  } else if (amountCandidates.length) {
    // Fallback: largest amount seen anywhere
    chosenAmount = amountCandidates.reduce((best, curr) =>
      !best || curr.value > best.value ? curr : best,
    );
  }
  if (chosenAmount) {
    result.amount = chosenAmount.value.toFixed(2);
  }

  // --- Date: DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, etc. ---
  const dateMatch =
    text.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/) ??
    text.match(/(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
  if (dateMatch) {
    const d = dateMatch[3].length === 4
      ? [dateMatch[3], dateMatch[1], dateMatch[2]]
      : [dateMatch[1], dateMatch[2], dateMatch[3]];
    const y = d[0].length === 2 ? `20${d[0]}` : d[0];
    result.date = `${y}-${d[1].padStart(2, '0')}-${d[2].padStart(2, '0')}`;
  }

  // --- Vendor / description: first reasonable non-numeric line near top (excluding generic words) ---
  const vendorStopWords =
    /(receipt|tax invoice|invoice|gst|abn|nzbn|date|time|table|order|cashier|server|total|amount|sum|balance|eftpos|visa|mastercard|card|cash)/i;
  const dateLike = /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/;

  const vendorLine =
    lines.find(
      (l, idx) =>
        idx <= 8 &&
        l.length > 2 &&
        !vendorStopWords.test(l) &&
        !dateLike.test(l) &&
        // avoid lines that are almost all numbers / prices
        !/^\$?\s*[\d.,]+\s*$/.test(l),
    ) ?? '';

  if (vendorLine) {
    result.vendor = vendorLine.slice(0, 200);
    result.description = result.vendor;
  } else {
    // Fallback: first non-empty non-numeric line anywhere
    const descLine = lines.find(
      (l) =>
        l.length > 2 &&
        !/^\d+\.?\d*$/.test(l) &&
        !dateLike.test(l) &&
        !/^(receipt|invoice|date|total|amount|sum)$/i.test(l),
    );
    if (descLine) result.description = descLine.slice(0, 200);
  }

  // --- Line items: collect lines that look like "items with prices", exclude totals / GST etc. ---
  const summaryLines: string[] = [];
  const totalIndex =
    chosenAmount && totalCandidates.length
      ? chosenAmount.index
      : Number.POSITIVE_INFINITY;

  const itemSkipWords =
    /(total|subtotal|grand total|gst|tax|amount due|balance|eftpos|visa|mastercard|card|cash|change)/i;
  const headerWords = /(qty|quantity|price|unit|item|description)/i;

  lines.forEach((line, idx) => {
    if (!line) return;
    if (idx >= totalIndex) return;
    const lower = line.toLowerCase();
    if (itemSkipWords.test(lower)) return;
    if (headerWords.test(lower)) return;
    if (!/[a-zA-Z]/.test(line)) return; // need some text
    if (!moneyRegex.test(line)) return; // needs a price-like number
    summaryLines.push(line);
  });

  if (summaryLines.length) {
    result.itemsNote = summaryLines.join('\n').slice(0, 800);
  }

  return result;
}

/**
 * Run OCR on a receipt image using client-side Tesseract with basic preprocessing.
 * Vertex AI Gemini is used afterwards to understand the text.
 */
async function recognizeReceiptWithOcr(file: File): Promise<string> {
  const imageUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = (e) => reject(e);
      i.src = imageUrl;
    });

    const maxDimension = 1600;
    const scale = Math.min(maxDimension / Math.max(img.width, img.height), 1);
    const width = Math.round(img.width * scale);
    const height = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      const Tesseract = await import('tesseract.js');
      const { data } = await Tesseract.recognize(file, 'eng');
      return data.text;
    }
    ctx.drawImage(img, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    const dataArr = imageData.data;
    for (let i = 0; i < dataArr.length; i += 4) {
      const r = dataArr[i];
      const g = dataArr[i + 1];
      const b = dataArr[i + 2];
      let v = 0.299 * r + 0.587 * g + 0.114 * b;
      v = v < 128 ? v * 0.8 : Math.min(255, v * 1.2);
      const vv = v;
      dataArr[i] = vv;
      dataArr[i + 1] = vv;
      dataArr[i + 2] = vv;
    }
    ctx.putImageData(imageData, 0, 0);

    const Tesseract = await import('tesseract.js');
    const { data } = await Tesseract.recognize(canvas, 'eng', {
      tessedit_char_whitelist:
        '0123456789.$:/-ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz ',
    } as any);
    return data.text;
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

interface GeminiReceiptResult {
  vendorName?: string;
  transactionType?: string;
  totalAmount?: number;
  date?: string | null;
  itemsNote?: string;
}

async function interpretReceiptWithGemini(ocrText: string): Promise<GeminiReceiptResult | null> {
  try {
    const prompt = [
      'You are a tool that extracts structured information from till receipts for accounting.',
      'Return ONLY valid JSON with this exact shape, no extra text, no markdown:',
      '',
      '{',
      '  "vendorName": string,      // store / restaurant name',
      '  "transactionType": string, // short category like "Fuel", "Cleaning", "Groceries"',
      '  "totalAmount": number,     // final total including tax',
      '  "date": string | null,     // ISO date YYYY-MM-DD if you can infer it, else null',
      '  "itemsNote": string        // multi-line summary of line items and prices',
      '}',
      '',
      'If a field is unknown, use null (for date) or empty string.',
      '',
      'Receipt OCR text:',
      ocrText,
    ].join('\n');

    const result = await receiptModel.generateContent(prompt);
    const aiText = result.response.text().trim();
    const jsonString = aiText.replace(/```json|```/g, '').trim();
    const parsed = JSON.parse(jsonString) as GeminiReceiptResult;
    return parsed;
  } catch (err) {
    console.error('Gemini receipt parsing failed:', err);
    return null;
  }
}

function formatInputDate(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getDateNDaysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return formatInputDate(d);
}

const EXPENSE_CATEGORIES = GRANULAR_EXPENSE_CATEGORIES;

const firebaseApp = initializeFirebase();
const ai = getAI(firebaseApp, { backend: new VertexAIBackend('us-central1') });
const receiptModel = getGenerativeModel(ai, { model: 'gemini-2.5-flash' });

interface ExpensesPageProps {
  currentUser: CurrentUser | null;
}

const ExpensesPage: React.FC<ExpensesPageProps> = ({ currentUser }) => {
  const displayStoredCategory = (c: string | undefined) => {
    if (!c || !c.trim()) return '—';
    return migrateLegacyExpenseCategoryDisplay(c) || c;
  };
  const [expenseStartDate, setExpenseStartDate] = useState(() => getDateNDaysAgo(30));
  const [expenseEndDate, setExpenseEndDate] = useState(() => formatInputDate(new Date()));
  const [expenseList, setExpenseList] = useState<DailyExpense[]>([]);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [expenseError, setExpenseError] = useState<string | null>(null);
  const [expenseForm, setExpenseForm] = useState({
    date: formatInputDate(new Date()),
    vendorName: '',
    description: '',
    amount: '',
    category: '',
    receiptNote: '',
  });
  const [expenseReceiptFile, setExpenseReceiptFile] = useState<File | null>(null);
  const [receiptFilesQueue, setReceiptFilesQueue] = useState<File[]>([]);
  const [currentReceiptIndex, setCurrentReceiptIndex] = useState(0);
  const [expenseSaving, setExpenseSaving] = useState(false);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState<string | null>(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [parsingReceipt, setParsingReceipt] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const clearReceipt = useCallback(() => {
    setReceiptFilesQueue((queue) => {
      let nextQueue = queue;
      let nextIndex = currentReceiptIndex;

      if (queue.length > 0) {
        nextQueue = queue.filter((_, idx) => idx !== currentReceiptIndex);
        if (nextQueue.length === 0) {
          nextIndex = 0;
        } else if (currentReceiptIndex >= nextQueue.length) {
          nextIndex = nextQueue.length - 1;
        }
      }

      if (receiptPreviewUrl) {
        URL.revokeObjectURL(receiptPreviewUrl);
      }

      if (nextQueue.length > 0) {
        const nextFile = nextQueue[nextIndex];
        setExpenseReceiptFile(nextFile);
        setReceiptPreviewUrl(URL.createObjectURL(nextFile));
      } else {
        setExpenseReceiptFile(null);
        setReceiptPreviewUrl(null);
      }

      setCurrentReceiptIndex(nextIndex);

      setExpenseForm((f) => ({
        ...f,
        vendorName: '',
        description: '',
        amount: '',
        category: '',
        receiptNote: '',
      }));

      return nextQueue;
    });
  }, [currentReceiptIndex, receiptPreviewUrl]);

  useEffect(() => {
    return () => {
      if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [receiptPreviewUrl]);

  const loadExpenses = useCallback(async () => {
    setExpenseLoading(true);
    setExpenseError(null);
    try {
      const list = await dailyExpensesService.getByDateRange(expenseStartDate, expenseEndDate);
      setExpenseList(list);
    } catch (error) {
      console.error('Failed to load expenses:', error);
      setExpenseError('Failed to load expenses. Please try again.');
    } finally {
      setExpenseLoading(false);
    }
  }, [expenseStartDate, expenseEndDate]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    if (!showCameraModal) return;
    let stream: MediaStream | null = null;
    const video = videoRef.current;
    const assignStream = (s: MediaStream) => {
      const v = videoRef.current;
      if (v) v.srcObject = s;
    };
    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      })
      .then((s) => {
        stream = s;
        streamRef.current = s;
        assignStream(s);
        if (!videoRef.current) setTimeout(() => assignStream(s), 100);
      })
      .catch((err) => {
        console.error('Camera error:', err);
        setExpenseError('Could not access camera. Try uploading a file instead.');
        setShowCameraModal(false);
      });
    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [showCameraModal]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <header className="bg-white shadow-md z-10 flex-shrink-0 safe-area-inset-top">
        <div className="max-w-full mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between min-h-[52px] sm:h-16 py-2 sm:py-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Expenses</h1>
          </div>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6 min-h-0">
        <div className="max-w-5xl mx-auto">
          <section className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-4 sm:px-6 py-4 sm:py-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Daily Expenses</h2>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5 max-w-md">
                    Record expenses and attach receipts. Export to CSV.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="flex items-center gap-2 min-h-[44px]">
                      <label className="text-sm text-gray-600 shrink-0">From</label>
                      <input
                        type="date"
                        value={expenseStartDate}
                        onChange={(e) => setExpenseStartDate(e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 touch-manipulation"
                      />
                    </div>
                    <div className="flex items-center gap-2 min-h-[44px]">
                      <label className="text-sm text-gray-600 shrink-0">To</label>
                      <input
                        type="date"
                        value={expenseEndDate}
                        onChange={(e) => setExpenseEndDate(e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 touch-manipulation"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={loadExpenses}
                      disabled={expenseLoading}
                      className="min-h-[44px] px-4 py-2.5 sm:py-2 text-base sm:text-sm font-medium rounded-lg border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors touch-manipulation"
                    >
                      {expenseLoading ? 'Loading…' : 'Load'}
                    </button>
                    {expenseList.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          const headers = ['Date', 'Vendor', 'Type of transaction', 'Amount', 'Category', 'Receipt Note', 'Receipt URL', 'Created By', 'Created At'];
                          const rows = expenseList.map((e) => [
                            e.date,
                            (e.vendorName ?? '').replace(/"/g, '""'),
                            (e.description ?? '').replace(/"/g, '""'),
                            String(e.amount),
                            (e.category ?? '').replace(/"/g, '""'),
                            (e.receiptNote ?? '').replace(/"/g, '""'),
                            e.receiptUrl ?? '',
                            (e.createdByName ?? '').replace(/"/g, '""'),
                            e.createdAt ? new Date(e.createdAt).toISOString() : '',
                          ]);
                          const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n');
                          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                          const a = document.createElement('a');
                          a.href = URL.createObjectURL(blob);
                          a.download = `daily-expenses-${expenseStartDate}-to-${expenseEndDate}.csv`;
                          a.click();
                          URL.revokeObjectURL(a.href);
                        }}
                        className="min-h-[44px] px-4 py-2.5 sm:py-2 text-base sm:text-sm font-medium rounded-lg bg-pink-600 text-white hover:bg-pink-700 transition-colors touch-manipulation"
                      >
                        Export CSV
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6">
              {expenseError && (
                <div className="rounded-lg bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
                  {expenseError}
                </div>
              )}

              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-4">Add expense</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                    <input
                      type="date"
                      value={expenseForm.date}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, date: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 touch-manipulation"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Vendor name</label>
                    <input
                      type="text"
                      placeholder="e.g. Countdown, BP"
                      value={expenseForm.vendorName}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, vendorName: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 touch-manipulation"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Type of transaction</label>
                    <input
                      type="text"
                      placeholder="e.g. Cleaning, Fuel, Office supplies"
                      value={expenseForm.description}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, description: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 touch-manipulation"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, amount: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 touch-manipulation"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
                    <select
                      value={expenseForm.category}
                      onChange={(e) => setExpenseForm((f) => ({ ...f, category: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-white touch-manipulation"
                    >
                      {EXPENSE_CATEGORIES.map((cat) => (
                        <option key={cat || '_blank'} value={cat}>
                          {cat || '— Select category —'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-0 w-full sm:min-w-[200px]">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Receipt note (optional)</label>
                      <input
                        type="text"
                        placeholder="e.g. Invoice #1234"
                        value={expenseForm.receiptNote}
                        onChange={(e) => setExpenseForm((f) => ({ ...f, receiptNote: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] sm:min-h-0 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 touch-manipulation"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-gray-500 w-full sm:w-auto">Receipt:</span>
                    <button
                      type="button"
                      onClick={() => setShowCameraModal(true)}
                      className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 touch-manipulation"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 13v7a2 2 0 01-2 2H7a2 2 0 01-2-2v-7" /></svg>
                      Take photo
                    </button>
                    <label className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 cursor-pointer touch-manipulation">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="sr-only"
                        onChange={(e) => {
                          const files = Array.from(e.target.files ?? []);
                          if (files.length > 0) {
                            const first = files[0];
                            if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
                            setReceiptFilesQueue(files);
                            setCurrentReceiptIndex(0);
                            setExpenseReceiptFile(first);
                            setReceiptPreviewUrl(URL.createObjectURL(first));
                          }
                          e.target.value = '';
                        }}
                      />
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      Upload file
                    </label>
                    {expenseReceiptFile && (
                      <>
                        <button
                          type="button"
                          disabled={parsingReceipt}
                          className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm hover:bg-amber-100 disabled:opacity-50 touch-manipulation"
                          onClick={async () => {
                            if (!expenseReceiptFile) return;
                            setParsingReceipt(true);
                            setExpenseError(null);
                            try {
                              const text = await recognizeReceiptWithOcr(expenseReceiptFile);
                              const aiParsed = await interpretReceiptWithGemini(text);
                              const parsed = parseReceiptText(text);
                              setExpenseForm((f) => {
                                const amountFromAi = aiParsed?.totalAmount;
                                const dateFromAi = aiParsed?.date ?? undefined;
                                const vendorFromAi = aiParsed?.vendorName;
                                const itemsFromAi = aiParsed?.itemsNote;

                                const amountString =
                                  amountFromAi != null && !Number.isNaN(amountFromAi)
                                    ? amountFromAi.toFixed(2)
                                    : parsed.amount || f.amount;

                                const combinedNote = [f.receiptNote, itemsFromAi || parsed.itemsNote || '']
                                  .filter((part) => part && part.trim().length > 0)
                                  .join('\n')
                                  .slice(0, 1000);

                                return {
                                  ...f,
                                  vendorName: vendorFromAi || parsed.vendor || f.vendorName,
                                  amount: amountString,
                                  date: dateFromAi || parsed.date || f.date,
                                  // Leave description (type of transaction) for the user to confirm / edit
                                  description: f.description,
                                  receiptNote: combinedNote || f.receiptNote,
                                };
                              });
                            } catch (err) {
                              console.error('OCR failed:', err);
                              setExpenseError('Could not parse receipt. Try uploading a clearer image.');
                            } finally {
                              setParsingReceipt(false);
                            }
                          }}
                        >
                          {parsingReceipt ? 'Parsing…' : 'Parse from receipt'}
                        </button>
                        <button
                          type="button"
                          onClick={clearReceipt}
                          className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 touch-manipulation"
                        >
                          Clear receipt
                        </button>
                      </>
                    )}
                  </div>
                  {(expenseReceiptFile || receiptPreviewUrl) && (
                    <div className="flex items-center gap-3">
                      {receiptPreviewUrl && (
                        <img src={receiptPreviewUrl} alt="Receipt preview" className="h-20 w-auto rounded border border-gray-200 object-contain bg-gray-50" />
                      )}
                      <span className="text-sm text-gray-500">
                        {expenseReceiptFile ? expenseReceiptFile.name : 'Receipt attached'} — saved with expense when you click Add expense
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-4 flex flex-wrap items-end gap-3">
                  <button
                    type="button"
                    disabled={
                      expenseSaving ||
                      !expenseForm.vendorName.trim() ||
                      !expenseForm.description.trim() ||
                      !expenseForm.amount.trim()
                    }
                    className="w-full sm:w-auto min-h-[48px] px-5 py-3 sm:py-2.5 text-base sm:text-sm font-medium rounded-lg bg-pink-600 text-white hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors touch-manipulation"
                    onClick={async () => {
                      const amount = parseFloat(expenseForm.amount);
                      if (Number.isNaN(amount) || amount < 0) return;
                      setExpenseSaving(true);
                      setExpenseError(null);
                      try {
                        const created = await dailyExpensesService.create({
                          date: expenseForm.date,
                          vendorName: expenseForm.vendorName.trim() || undefined,
                          description: expenseForm.description.trim(),
                          amount,
                          category: expenseForm.category.trim() || undefined,
                          receiptNote: expenseForm.receiptNote.trim() || undefined,
                          createdAt: Date.now(),
                          createdBy: currentUser?.id,
                          createdByName: currentUser?.name ?? currentUser?.email ?? undefined,
                        });
                        if (expenseReceiptFile) {
                          const { url, fileName } = await uploadExpenseReceipt(created.id, expenseReceiptFile);
                          await dailyExpensesService.update(created.id, { receiptUrl: url, receiptFileName: fileName });
                        }
                        // Reset fields for the next expense but keep the date
                        setExpenseForm((f) => ({
                          ...f,
                          vendorName: '',
                          description: '',
                          amount: '',
                          category: '',
                          receiptNote: '',
                        }));

                        // Advance to the next receipt in the queue (if any)
                        setReceiptFilesQueue((queue) => {
                          if (queue.length === 0) {
                            if (receiptPreviewUrl) {
                              URL.revokeObjectURL(receiptPreviewUrl);
                            }
                            setExpenseReceiptFile(null);
                            setReceiptPreviewUrl(null);
                            setCurrentReceiptIndex(0);
                            return queue;
                          }

                          const nextIndex = currentReceiptIndex + 1;
                          if (nextIndex < queue.length) {
                            const nextFile = queue[nextIndex];
                            if (receiptPreviewUrl) {
                              URL.revokeObjectURL(receiptPreviewUrl);
                            }
                            setExpenseReceiptFile(nextFile);
                            setReceiptPreviewUrl(URL.createObjectURL(nextFile));
                            setCurrentReceiptIndex(nextIndex);
                            return queue;
                          }

                          // No more receipts left
                          if (receiptPreviewUrl) {
                            URL.revokeObjectURL(receiptPreviewUrl);
                          }
                          setExpenseReceiptFile(null);
                          setReceiptPreviewUrl(null);
                          setCurrentReceiptIndex(0);
                          return [];
                        });

                        await loadExpenses();
                      } catch (err) {
                        console.error('Failed to add expense:', err);
                        setExpenseError('Failed to add expense. Please try again.');
                      } finally {
                        setExpenseSaving(false);
                      }
                    }}
                  >
                    {expenseSaving ? 'Saving…' : 'Add expense'}
                  </button>
                </div>
              </div>

              {expenseList.length > 0 ? (
                <>
                  {/* Mobile: card list */}
                  <div className="sm:hidden space-y-3 max-h-[28rem] overflow-y-auto">
                    {expenseList.map((e) => (
                      <div key={e.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <p className="font-medium text-gray-900">
                              {e.vendorName || e.description || 'Expense'}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {e.description || '—'} · {e.date} · {e.category ? displayStoredCategory(e.category) : 'No category'}
                            </p>
                          </div>
                          <p className="text-lg font-semibold text-gray-900 shrink-0">${Number(e.amount).toFixed(2)}</p>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {e.receiptUrl ? (
                            <a href={e.receiptUrl} target="_blank" rel="noopener noreferrer" className="min-h-[44px] inline-flex items-center px-3 py-2 rounded-lg bg-pink-50 text-pink-700 text-sm font-medium touch-manipulation">
                              View receipt
                            </a>
                          ) : null}
                          <button
                            type="button"
                            className="min-h-[44px] inline-flex items-center px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-medium touch-manipulation"
                            onClick={async () => {
                              if (!window.confirm('Delete this expense?')) return;
                              try {
                                await dailyExpensesService.remove(e.id);
                                await loadExpenses();
                              } catch (err) {
                                console.error('Delete expense failed:', err);
                                setExpenseError('Failed to delete expense.');
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Desktop: table */}
                  <div className="hidden sm:block rounded-xl border border-gray-200 overflow-hidden">
                    <div className="max-h-[28rem] overflow-y-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Description</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Category</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Receipt</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Created</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700 w-20">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {expenseList.map((e) => (
                            <tr key={e.id} className="hover:bg-gray-50/80 transition-colors">
                              <td className="px-4 py-3 whitespace-nowrap text-gray-600">{e.date}</td>
                              <td className="px-4 py-3 text-gray-900">
                                <div className="font-medium">{e.vendorName || e.description || 'Expense'}</div>
                                <div className="text-xs text-gray-500">
                                  {e.description || '—'} {e.category ? `· ${displayStoredCategory(e.category)}` : ''}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-gray-900">${Number(e.amount).toFixed(2)}</td>
                              <td className="px-4 py-3">
                                <span className="text-gray-600">{displayStoredCategory(e.category)}</span>
                              </td>
                              <td className="px-4 py-3">
                                {e.receiptUrl ? (
                                  <a href={e.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700 font-medium">
                                    View receipt
                                  </a>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-500 text-xs">
                                {e.createdByName ?? '—'}
                                {e.createdAt ? ` · ${new Date(e.createdAt).toLocaleString()}` : ''}
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                                  onClick={async () => {
                                    if (!window.confirm('Delete this expense?')) return;
                                    try {
                                      await dailyExpensesService.remove(e.id);
                                      await loadExpenses();
                                    } catch (err) {
                                      console.error('Delete expense failed:', err);
                                      setExpenseError('Failed to delete expense.');
                                    }
                                  }}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                !expenseLoading && (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center">
                    <p className="text-gray-500">No expenses in this range.</p>
                    <p className="text-sm text-gray-400 mt-1">Choose dates and click Load, or add an expense above.</p>
                  </div>
                )
              )}
            </div>
          </section>
        </div>
      </main>

      {showCameraModal && (
        <div className="fixed inset-0 z-50 flex flex-col sm:items-center sm:justify-center bg-black sm:bg-black/70 p-0 sm:p-4">
          <div className="bg-white sm:rounded-xl shadow-xl w-full h-full sm:max-h-[90vh] sm:max-w-lg sm:w-full overflow-hidden flex flex-col">
            <div className="p-3 sm:p-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <h3 className="font-semibold text-gray-900 text-base sm:text-lg">Take receipt photo</h3>
              <button
                type="button"
                onClick={() => {
                  if (streamRef.current) {
                    streamRef.current.getTracks().forEach((t) => t.stop());
                    streamRef.current = null;
                  }
                  setShowCameraModal(false);
                }}
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 touch-manipulation"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 min-h-0 p-2 sm:p-4 bg-gray-900 flex items-center justify-center">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="max-h-full w-full object-contain"
              />
            </div>
            <div className="p-3 sm:p-4 flex gap-2 flex-shrink-0 safe-area-inset-bottom">
              <button
                type="button"
                className="flex-1 min-h-[48px] sm:min-h-[44px] py-3 sm:py-2.5 rounded-lg bg-pink-600 text-white font-medium hover:bg-pink-700 text-base sm:text-sm touch-manipulation"
                onClick={async () => {
                  const video = videoRef.current;
                  if (!video || !video.srcObject) return;
                  const canvas = document.createElement('canvas');
                  canvas.width = video.videoWidth;
                  canvas.height = video.videoHeight;
                  canvas.getContext('2d')?.drawImage(video, 0, 0);
                  canvas.toBlob(
                    (blob) => {
                      if (!blob) return;
                      const file = new File([blob], `receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
                      if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
                      setExpenseReceiptFile(file);
                      setReceiptPreviewUrl(URL.createObjectURL(blob));
                      if (streamRef.current) {
                        streamRef.current.getTracks().forEach((t) => t.stop());
                        streamRef.current = null;
                      }
                      setShowCameraModal(false);
                    },
                    'image/jpeg',
                    0.9
                  );
                }}
              >
                Capture
              </button>
              <button
                type="button"
                className="min-h-[48px] sm:min-h-[44px] px-4 py-3 sm:py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 touch-manipulation"
                onClick={() => {
                  if (streamRef.current) {
                    streamRef.current.getTracks().forEach((t) => t.stop());
                    streamRef.current = null;
                  }
                  setShowCameraModal(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
